<template>
  <div>
    <div class="bg-white rounded-4 box-shadow-1 p-2 p-md-4 pt-md-4 pt-3">
      <h2>
        {{ this.action === 'create' ? 'Zadejte inzerát' : 'Editace inzerátu' }}
      </h2>
      <Stepper
        :steps="stepNames"
        :current-step="currentStep"
        @update-current-step="updateCurrentStep"
      />

      <transition name="fade">
        <div
          v-if="notification"
          ref="notificationBox"
          class="p-3 mb-4 mt-0"
          :style="notificationStyle"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <p v-if="notification.message" class="mb-2">{{ notification.message }}</p>
              <ul v-if="notification.items && notification.items.length" class="mb-0 ps-3">
                <li v-for="(item, index) in notification.items" :key="index">
                  {{ item }}
                </li>
              </ul>
            </div>
            <button
              @click="clearNotification"
              type="button"
              class="btn-close ms-3"
              aria-label="Close"
            >
              <i class="icon icon--md icon-dismiss"></i>
            </button>
          </div>
        </div>
      </transition>

      <Wizard
        :steps="steps"
        :action="this.action"
        @show-notification="showNotification"
        @prefill-complete="handlePrefillComplete"
      />

      <div v-if="currentStep < steps.length - 1 && !isVinPrefillStep">
        <p class="small mt-6">
          <span class="text-primary">*</span> Před pokračováním vyplňte všechny
          povinná pole.
        </p>
        <div class="d-flex gap-2 flex-column flex-md-row mt-6">
          <button
            v-if="currentStep > 0"
            class="btn btn-lg btn-outline-gray mr-auto"
            @click.prevent="updateCurrentStep(currentStep - 1)"
            :disabled="isLoading"
          >
            Zpět
          </button>
          <button
            class="btn btn-lg btn-gray"
            @click.prevent="updateCurrentStep(currentStep + 1)"
            :disabled="isLoading"
          >
            Pokračovat
          </button>
        </div>
      </div>

      <div
        v-if="currentStep === steps.length - 1"
        class="d-flex gap-2 flex-column flex-md-row mt-6"
      >
        <button
          class="btn btn-lg btn-outline-gray mr-auto"
          @click.prevent="updateCurrentStep(currentStep - 1)"
          :disabled="isLoading"
        >
          Zpět
        </button>
        <button
          v-if="action === 'create' || action === 'update' && !advertisement?.isPublished"
          @click="beforeCreateAdvertisement('draft')"
          class="btn btn-lg btn-outline-gray"
          :disabled="isLoading"
        >
          Zobrazit náhled
        </button>
        <button
          @click="beforeCreateAdvertisement('published')"
          class="btn btn-lg btn-gray"
          :disabled="isLoading"
        >
          Publikovat inzerát
        </button>
      </div>
    </div>
  </div>

  <SingleSelectionModal
    modal-id="single-brand-modal"
    :options="brands"
    title="Vyberte značku"
    :cssClasses="['modal-xsm']"
    @selected="selectBrand"
    option-wrapper-style="col-12 mb-1"
    :saved-option="selectedBrand"
  />
  <SingleSelectionModal
    modal-id="single-model-modal"
    :options="models"
    title="Vyberte model"
    :cssClasses="['modal-xsm']"
    @selected="selectModel"
    option-wrapper-style="col-12 mb-1"
    :saved-option="selectedModel"
  />
  <SingleImageSelectionModal
    modal-id="single-body-modal"
    :options="bodies"
    title="Vyberte karosérii"
    :cssClasses="['modal-xsm']"
    :columns="2"
    @selected="selectBody"
    :saved-option="selectedBody"
  />
  <SingleSelectionModal
    modal-id="single-seats-count-modal"
    :options="seatsCounts"
    title="Vyberte počet sedadel"
    :cssClasses="['modal-xsm']"
    @selected="selectSeatsCount"
    option-wrapper-style="col-12 mb-1"
    :saved-option="selectedSeatsCount"
  />
  <SingleSelectionModal
    modal-id="single-gearbox-modal"
    :options="gearboxes"
    title="Vyberte převodovku"
    :cssClasses="['modal-xsm']"
    @selected="selectGearbox"
    option-wrapper-style="col-12 mb-1"
    :saved-option="selectedGearbox"
  />
  <SingleSelectionModal
    modal-id="single-wheel-drive-modal"
    :options="wheelDrives"
    title="Vyberte pohon"
    :cssClasses="['modal-xsm']"
    @selected="selectWheelDrive"
    option-wrapper-style="col-12 mb-1"
    :saved-option="selectedWheelDrive"
  />
  <SingleSelectionModal
    modal-id="single-fuel-modal"
    :options="fuels"
    title="Vyberte palivo"
    :cssClasses="['modal-xsm']"
    @selected="selectFuel"
    option-wrapper-style="col-12 mb-1"
    :saved-option="selectedFuel"
  />
  <SingleScrollSelectionModal
    modal-id="single-displacement-modal"
    :options="displacements"
    title="Vyberte objem"
    :cssClasses="['modal-xs']"
    name="displacement-radio"
    unit="ccm"
    @selected="selectDisplacement"
    :saved-option="selectedDisplacement"
  />
  <SingleScrollSelectionModal
    modal-id="single-engine-power-modal"
    :options="enginePowers"
    title="Vyberte výkon"
    :cssClasses="['modal-xs']"
    name="engine-power-radio"
    unit="kW"
    @selected="selectEnginePower"
    :saved-option="selectedEnginePower"
  />
  <SingleSelectionModal
    modal-id="single-car-state-modal"
    :options="carStates"
    title="Vyberte stav vozu"
    :cssClasses="['modal-xsm']"
    option-wrapper-style="col-12 mb-1"
    @selected="selectCarState"
    :saved-option="selectedCarState"
  />
  <SingleScrollSelectionModal
    modal-id="single-rides-since-year-modal"
    :options="ridesSinceYears"
    title="Vyberte rok"
    :cssClasses="['modal-sm']"
    :formatted="false"
    name="rides-since-year-radio"
    @selected="selectRidesSinceYear"
  />
  <SingleSelectionModal
    modal-id="single-country-origin-modal"
    :options="countries"
    title="Vyberte zemi"
    :cssClasses="['modal-xsm']"
    option-wrapper-style="col-12 mb-1"
    @selected="selectCountryOrigin"
    :saved-option="selectedCountryOrigin"
  />
  <UploadImagesModal modal-id="upload-images" :cssClasses="['modal-xxl']" />
  <ModalAlert />
  <ModalAdvertisementCreated />
</template>

<script>
import ApiService from '../../services/apiService.js';
import HelperService from '../../services/helperService.js';
import { isEmpty } from 'lodash';
import { computed, markRaw, onBeforeMount, ref } from 'vue';
import FormSelect from '../FormSelect.vue';
import SingleSelectionModal from '../modals/SingleSelectionModal.vue';
import SingleImageSelectionModal from '../modals/SingleImageSelectionModal.vue';
import FormBoolean from '../FormBoolean.vue';
import FormInput from '../FormInput.vue';
import FormTextArea from '../FormTextArea.vue';
import CarEquipmentList from './CarEquipmentList.vue';
import SingleScrollSelectionModal from '../modals/SingleScrollSelectionModal.vue';
import UploadImagesModal from '../modals/UploadImagesModal.vue';
import Stepper from './wizard/Stepper.vue';

import Wizard from './wizard/index.vue';
import VinPrefillStep from './steps/VinPrefillStep.vue';
import BaseInfoStep from './steps/BaseInfoStep.vue';
import MoreInfoStep from './steps/MoreInfoStep.vue';
import CarEquipmentStep from './steps/CarEquipmentStep.vue';
import ContactInfoStep from './steps/ContactInfoStep.vue';
import { useStore } from 'vuex';
import ModalAlert from '../modals/ModalAlert.vue';
import ModalAdvertisementCreated from '../modals/ModalAdvertisementCreated.vue';

export default {
  name: 'AdvertisementForm',

  components: {
    ModalAdvertisementCreated,
    ModalAlert,
    UploadImagesModal,
    CarEquipmentList,
    FormTextArea,
    FormInput,
    FormBoolean,
    SingleSelectionModal,
    SingleImageSelectionModal,
    SingleScrollSelectionModal,
    FormSelect,
    Wizard,
    Stepper,
  },

  setup(props) {
    const store = useStore();
    const currentStep = computed(
      () => store.state.AdvertisementStore.currentStep
    );

    // Build steps array based on action (create or update)
    const allSteps = [
      {
        stepperProps: { title: '1. Předvyplnění z VIN' },
        component: markRaw(VinPrefillStep),
        validationCase: 1,
        showOnCreate: true,
        showOnUpdate: false,
      },
      {
        stepperProps: { title: '2. Základní údaje' },
        component: markRaw(BaseInfoStep),
        validationCase: 2,
        showOnCreate: true,
        showOnUpdate: true,
      },
      {
        stepperProps: { title: '3. Rozšířené informace a foto' },
        component: markRaw(MoreInfoStep),
        validationCase: 3,
        showOnCreate: true,
        showOnUpdate: true,
      },
      {
        stepperProps: { title: '4. Výbava vozu' },
        component: markRaw(CarEquipmentStep),
        validationCase: 4,
        showOnCreate: true,
        showOnUpdate: true,
      },
      {
        stepperProps: { title: '5. Kontaktní údaje' },
        component: markRaw(ContactInfoStep),
        validationCase: 5,
        showOnCreate: true,
        showOnUpdate: true,
      },
    ];

    // Filter steps based on action
    const isCreate = props.action === 'create';
    const filteredSteps = allSteps.filter(step =>
      isCreate ? step.showOnCreate : step.showOnUpdate
    );

    // Renumber step titles for update mode
    const steps = filteredSteps.map((step, index) => ({
      ...step,
      stepperProps: {
        ...step.stepperProps,
        title: step.stepperProps.title.replace(/^\d+\./, `${index + 1}.`)
      }
    }));

    const stepNames = steps.map((item) => item.stepperProps.title);

    onBeforeMount(() => {
      store.commit('AdvertisementStore/updateCurrentStep', 0); // Reset wizard on refresh / load
    });

    return {
      steps,
      stepNames,
      currentStep,
    };
  },

  props: {
    advertisementId: {
      type: Number,
      default: undefined,
    },
    action: {
      type: String,
      default: 'create',
    },
  },

  data() {
    return {
      advertisement: undefined,
      errors: [],
      isLoading: false,
      notification: null,
      notificationTimeout: null,
      brands: [],
      models: [],
      bodies: [],
      seatsCounts: [],
      gearboxes: [],
      wheelDrives: [],
      fuels: [],
      displacements: [],
      enginePowers: [],
      carStates: [],
      ridesSinceYears: [],
      countries: [],
    };
  },

  watch: {
    currentStep(newStep) {
      const headerElement = document.getElementById('advertisement-header');
      if (headerElement) {
        headerElement.style.display = newStep === 0 ? 'block' : 'none';
      }
    },

    async openModalId(value) {
      switch (value) {
        case 'single-brand-modal':
          const brands = await this.loadResource(
            `brands?model_type=${this.modelType}`
          );
          this.brands = brands.map((i) => {
            return { id: i.id, value: i.name, label: i.name };
          });
          break;
        case 'single-model-modal':
          const models = await this.loadResource(
            `models?brand_id=${this.selectedBrand.id}&model_type=${this.modelType}`
          );
          this.models = models.map((i) => {
            return { id: i.id, value: i.name, label: i.name };
          });
          break;
        case 'single-body-modal':
          const bodies = await this.loadResource(
            `bodies?model_type=${this.modelType}`
          );
          this.bodies = bodies.map((i) => {
            return {
              id: i.id,
              value: i.name,
              label: i.name,
              count: i.count,
              iconName: i.iconName,
            };
          });
          break;
        case 'single-seats-count-modal':
          const seatsCounts = await this.loadResource('seats');
          this.seatsCounts = seatsCounts.map((i) => {
            return { id: i.id, value: i.name, label: i.name };
          });
          break;
        case 'single-gearbox-modal':
          const gearboxes = await this.loadResource('gearboxes');
          this.gearboxes = gearboxes.map((i) => {
            return { id: i.id, value: i.name, label: i.name };
          });
          break;
        case 'single-wheel-drive-modal':
          const wheelDrives = await this.loadResource('wheel_drives');
          this.wheelDrives = wheelDrives.map((i) => {
            return { id: i.id, value: i.name, label: i.name };
          });
          break;
        case 'single-fuel-modal':
          const fuels = await this.loadResource('fuels');
          this.fuels = fuels.map((i) => {
            return { id: i.id, value: i.name, label: i.name };
          });
          break;
        case 'single-displacement-modal':
          const displacements = await this.loadResource('displacements');
          this.displacements = displacements.map((v, i) => {
            return { id: i.id, value: v.toString()};
          });
          break;
        case 'single-engine-power-modal':
          const enginePowers = await this.loadResource('engine_powers');
          this.enginePowers = enginePowers.map((v, i) => {
            return { id: i.id, value: v.toString() };
          });
          break;
        case 'single-car-state-modal':
          const carStates = await this.loadResource('car_states');
          this.carStates = carStates.map((i) => {
            return { id: i.id, value: i.name, label: i.name };
          });
          break;
        case 'single-rides-since-year-modal':
          const ridesSinceYears = await this.loadResource('rides_since_range');
          this.ridesSinceYears = ridesSinceYears.map((v, i) => {
            return { id: i.id, value: v.toString() }
          })
          break;
        case 'single-country-origin-modal':
          const countries = await this.loadResource('countries');
          this.countries = countries.map((i) => {
            return { id: i.id, value: i.name, label: i.name };
          });
          break;
      }
    },

    modelType() {
      this.$store.commit('AdvertisementStore/selectBrand', undefined);
      this.$store.commit('AdvertisementStore/selectModel', undefined);
    },
  },

  computed: {
    isVinPrefillStep() {
      // Check if current step is VIN prefill step (only in create mode, step 0)
      const currentStepData = this.steps[this.currentStep];
      return currentStepData && currentStepData.validationCase === 1;
    },
    modelType() {
      return this.$store.state.AdvertisementStore.advertisement.modelType;
    },
    openModalId() {
      return this.$store.state.ModalStore.openedModalId;
    },
    selectedBrand() {
      return this.$store.state.AdvertisementStore.advertisement.brand;
    },
    selectedModel() {
      return this.$store.state.AdvertisementStore.advertisement.model;
    },
    selectedBody() {
      return this.$store.state.AdvertisementStore.advertisement.body;
    },
    selectedSeatsCount() {
      return this.$store.state.AdvertisementStore.advertisement.seatsCount;
    },
    selectedGearbox() {
      return this.$store.state.AdvertisementStore.advertisement.gearbox;
    },
    selectedWheelDrive() {
      return this.$store.state.AdvertisementStore.advertisement.wheelDrive;
    },
    selectedFuel() {
      return this.$store.state.AdvertisementStore.advertisement.fuel;
    },
    selectedDisplacement() {
      return this.$store.state.AdvertisementStore.advertisement.displacement;
    },
    selectedEnginePower() {
      return this.$store.state.AdvertisementStore.advertisement.enginePower;
    },
    selectedCarState() {
      return this.$store.state.AdvertisementStore.advertisement.carState;
    },
    selectedRidesSinceYear() {
      return this.$store.state.AdvertisementStore.advertisement.ridesSinceYear;
    },
    selectedCountryOrigin() {
      return this.$store.state.AdvertisementStore.advertisement.countryOrigin;
    },
    selectedFirstOwner() {
      return this.$store.state.AdvertisementStore.advertisement.firstOwner;
    },
    filledMileage() {
      return this.$store.state.AdvertisementStore.advertisement.mileage;
    },
    filledVin() {
      return this.$store.state.AdvertisementStore.advertisement.vin;
    },
    filledSubtitle() {
      return this.$store.state.AdvertisementStore.advertisement.subtitle;
    },
    filledPrice() {
      return this.$store.state.AdvertisementStore.advertisement.price;
    },
    selectedVatDeduction() {
      return this.$store.state.AdvertisementStore.advertisement.vatDeduction;
    },
    advertisementText() {
      return this.$store.state.AdvertisementStore.advertisement
        .advertisementText;
    },
    carEquipments() {
      return this.$store.state.AdvertisementStore.advertisement.carEquipments;
    },
    customEquipments() {
      return this.$store.state.AdvertisementStore.advertisement
        .customEquipments;
    },
    authorName() {
      return this.$store.state.AdvertisementStore.advertisement.authorName;
    },
    contactEmail() {
      return this.$store.state.AdvertisementStore.advertisement.contactEmail;
    },
    contactPhone() {
      return this.$store.state.AdvertisementStore.advertisement.contactPhone;
    },
    region() {
      return this.$store.state.AdvertisementStore.advertisement.region;
    },
    authorDescription() {
      return this.$store.state.AdvertisementStore.advertisement
        .authorDescription;
    },
    street() {
      return this.$store.state.AdvertisementStore.advertisement.street;
    },
    zipCode() {
      return this.$store.state.AdvertisementStore.advertisement.zipCode;
    },
    city() {
      return this.$store.state.AdvertisementStore.advertisement.city;
    },
    districtName() {
      return this.$store.state.AdvertisementStore.advertisement.districtName;
    },
    images() {
      return this.$store.state.AdvertisementStore.advertisement.images;
    },
    modelInputDisabled() {
      return isEmpty(this.selectedBrand);
    },
    notificationStyle() {
      if (!this.notification) return {};

      const styles = {
        success: {
          background: 'linear-gradient(211.85deg, #d4edda 26.23%, #c3e6cb 83.38%)',
          color: '#155724',
          border: '1px solid #28a745',
          borderRadius: '15px',
        },
        error: {
          background: 'linear-gradient(211.85deg, #ffe4e6 26.23%, #fedadc 83.38%)',
          color: '#721c24',
          border: '1px solid #f3646c',
          borderRadius: '15px',
        },
      };

      return styles[this.notification.type] || styles.error;
    },
  },

  methods: {
    showNotification({ type, message, items = null, duration = 30000 }) {
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }

      this.notification = { type, message, items };

      // If duration is null or 0, notification is persistent (won't auto-dismiss)
      if (duration > 0) {
        this.notificationTimeout = setTimeout(() => {
          this.clearNotification();
        }, duration);
      }
    },

    clearNotification() {
      this.notification = null;
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
        this.notificationTimeout = null;
      }
    },

    handlePrefillComplete() {
      if (!this.checkForm(1)) {
        return;
      }
      this.$store.commit('AdvertisementStore/updateCurrentStep', 1);
    },

    beforeCreateAdvertisement(state) {
      // Get the validation case for the last step (ContactInfoStep)
      const lastStep = this.steps[this.steps.length - 1];
      const validationCase = lastStep ? lastStep.validationCase : 5;

      if (!this.checkForm(validationCase)) {
        return;
      }
      // When form action is create, send POST ...
      if (this.action === 'create') {
        this.createAdvertisement(state);
        // ... or send PUT as update
      } else if (this.action === 'update') {
        this.updateAdvertisement(state);
      }
    },

    buildAdvertisementPayload(state) {
      return {
        model_type: this.modelType,
        brand_id: this.selectedBrand.id,
        model_id: this.selectedModel.id,
        sub_title: this.filledSubtitle,
        displacement: this.selectedDisplacement.value,
        power: this.selectedEnginePower.value,
        price: parseFloat(this.filledPrice),
        mileage: parseFloat(this.filledMileage),
        fuel_id: this.selectedFuel.id,
        gearbox_id: this.selectedGearbox.id,
        wheel_drive_id: this.selectedWheelDrive.id,
        body_id: this.selectedBody.id,
        country_origin_id: this.selectedCountryOrigin.id,
        rides_since_year: this.selectedRidesSinceYear.value,
        seats_count_id: this.selectedSeatsCount.id,
        vin: this.filledVin,
        advertisement_text: this.advertisementText,
        car_equipments: this.carEquipments,
        custom_equipments: this.customEquipments,
        is_reserved: false,
        vat_deduction: this.selectedVatDeduction,
        first_owner: this.selectedFirstOwner,
        car_state_id: this.selectedCarState.id,
        images: this.images
          .filter(img => {
            // Case 1: Newly uploaded images (from upload modal) - must be done and have serverUuid
            if (img.serverUuid) {
              return img.status === 'done';
            }
            // Case 2: Existing images (from API when editing) - have uuid but no serverUuid/status
            // These are already in DB, so include them
            return img.uuid && !img.status;
          })
          .map((obj, index) => ({
            uuid: obj.serverUuid || obj.uuid, // Use serverUuid for new uploads, uuid for existing
            position: index + 1
          })),
        state: 'active',
        is_published: state === 'published',
      };
    },

    buildUserPayload() {
      return {
        name: this.authorName,
        email: this.contactEmail,
        phone: this.contactPhone ? this.contactPhone.replace(/\s/g, '') : this.contactPhone,
        region: this.region,
        description: this.authorDescription,
        street: this.street,
        zip_code: this.zipCode,
        city: this.city,
        district_name: this.districtName,
      };
    },

    async createAdvertisement(state) {
      this.isLoading = true;
      const payload = this.buildAdvertisementPayload(state);
      const userPayload = this.buildUserPayload();
      try {
        const { data } = await ApiService.post('advertisements', {
          advertisement: payload,
          user: userPayload,
        });

        //draft? redirect to advertisement
        if (state === 'draft') {
          window.location.href = data.redirectTo;
        } else {
          this.$store.dispatch('ModalStore/openModal', {
            id: 'modal-advertisement-created',
            advertisementId: data.advertisementId,
            advertisementType: data.advertisementType,
            advertisementTitle: data.advertisementTitle,
            created: true,
            advertisementUrl: data.redirectTo,
            advertisementsIndexUrl: data.advertisementsIndexUrl,
            title: data.title,
            msg: data.msg,
          });
          this.$store.commit('AdvertisementStore/reset');
        }

      } catch (error) {
        this.$store.dispatch('ModalStore/openModal', {
          id: 'modal-alert',
          message: error.response.data.msg,
        });
      }

      setTimeout(() => {
        this.isLoading = false;
      }, 1000);
    },

    async updateAdvertisement(state) {
      this.isLoading = true;
      const payload = this.buildAdvertisementPayload(state);
      const userPayload = this.buildUserPayload();
      try {
        const { data } = await ApiService.put(
          `advertisements/${this.advertisementId}`,
          {
            advertisement: payload,
            user: userPayload,
          }
        );
        this.$store.dispatch('ModalStore/openModal', {
          id: 'modal-advertisement-created',
          advertisementId: data.advertisementId,
          advertisementTitle: data.advertisementTitle,
          advertisementUrl: data.redirectTo,
          advertisementsIndexUrl: data.advertisementsIndexUrl,
          title: data.title,
          msg: data.msg,
        });
      } catch (error) {
        this.$store.dispatch('ModalStore/openModal', {
          id: 'modal-alert',
          message: error.response.data.msg,
        });
      }

      setTimeout(() => {
        this.isLoading = false;
      }, 1000);
    },

    checkForm(stepIndex) {
      this.errors = [];

      switch (stepIndex) {
        case 1:
          // VinPrefillStep validation
          if (isEmpty(this.selectedCarState))
            this.errors.push('Prosím vyberte stav vozu');
          if (HelperService.isBlank(this.filledVin))
            this.errors.push('Prosím vyplňte VIN');
          if (
              this.filledVin &&
              !/(?=.*\d|=.*[A-Z])(?=.*[A-Z])[A-Z0-9]{17}/.test(this.filledVin)
          ) {
            this.errors.push(
                'VIN musí obsahovat 17 znaků, nesmí obsahovat písmena O,I,Q. Na posledních 4 pozicích by měly být číslice.'
            );
          }
          break;
        case 2:
          // BaseInfoStep validation
          if (isEmpty(this.selectedBrand))
            this.errors.push('Prosím vyberte značku');
          if (isEmpty(this.selectedModel))
            this.errors.push('Prosím vyberte model');
          if (isEmpty(this.selectedBody))
            this.errors.push('Prosím vyberte karosérii');
          if (isEmpty(this.selectedSeatsCount))
            this.errors.push('Prosím vyberte počet sedadel');
          if (isEmpty(this.selectedGearbox))
            this.errors.push('Prosím vyberte převodovku');
          if (isEmpty(this.selectedWheelDrive))
            this.errors.push('Prosím vyberte pohon kol');
          if (isEmpty(this.selectedFuel))
            this.errors.push('Prosím vyberte palivo');
          if (isEmpty(this.selectedDisplacement))
            this.errors.push('Prosím vyberte objem motoru');
          if (isEmpty(this.selectedEnginePower))
            this.errors.push('Prosím vyberte výkon motoru');
          if (isEmpty(this.selectedRidesSinceYear))
            this.errors.push('Prosím vyberte rok uvedení do provozu');
          if (isEmpty(this.selectedCountryOrigin))
            this.errors.push('Prosím vyberte zemi původu');
          if (this.selectedFirstOwner === undefined)
            this.errors.push('Prosím vyberte, zda jste první majitel vozu');
          if (HelperService.isBlank(this.filledMileage))
            this.errors.push('Prosím vyplňte stav tachometru');
          if (this.filledMileage && this.filledMileage.length < 2) {
            this.errors.push(
              'Prosím ověřte stav tachometru, je příliš krátký.'
            );
          }
          break;
        case 3:
          // MoreInfoStep validation
          if (HelperService.isBlank(this.filledSubtitle))
            this.errors.push('Prosím vyplňte podtitul inzerátu');
          if (HelperService.isBlank(this.filledPrice))
            this.errors.push('Prosím vyplňte požadovanou cenu');
          if (this.selectedVatDeduction === undefined)
            this.errors.push('Prosím vyberte odpočet DPH');
          if (isEmpty(this.images))
            this.errors.push('Prosím nahrajte alespoň jednu fotografii');
          break;
        case 4:
          // CarEquipmentStep validation
          // No required fields
          break;
        case 5:
          // ContactInfoStep validation
          if (HelperService.isBlank(this.contactPhone))
            this.errors.push('Prosím vyplňte kontaktní telefon');
          if (HelperService.isBlank(this.city))
            this.errors.push('Prosím vyplňte lokalitu vozidla');
          if (HelperService.isBlank(this.zipCode))
            this.errors.push('Prosím vyplňte PSČ');
          if (HelperService.isBlank(this.districtName))
            this.errors.push('Prosím vyplňte okres');
          if (HelperService.isBlank(this.region))
            this.errors.push('Prosím vyberte kraj');
          if (this.contactPhone && this.contactPhone.replace(/\s/g, '').length !== 9) {
            this.errors.push(
              'Prosím zkontrolujte tvar telefonu. Musí obsahovat 9 číslic'
            );
          }
          break;
        default:
      }

      if (!isEmpty(this.errors)) {
        this.showNotification({
          type: 'error',
          message: 'Prosím opravte chyby:',
          items: this.errors,
          duration: 0, // Persistent - won't auto-dismiss
        });
        this.$nextTick(() => {
          if (this.$refs.notificationBox) {
            this.$refs.notificationBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return false;
      }
      return true;
    },

    updateCurrentStep(index) {
      // Only validate when moving FORWARD, allow going back without validation
      if (index > this.currentStep) {
        const currentStepData = this.steps[this.currentStep];
        const validationCase = currentStepData ? currentStepData.validationCase : this.currentStep;

        if (!this.checkForm(validationCase)) {
          return;
        }
      }

      if (index >= 0 && index < this.steps.length) {
        this.clearNotification();
        this.$store.commit('AdvertisementStore/updateCurrentStep', index);
      }
    },

    async loadResource(resource) {
      const { data } = await ApiService.get(resource);
      return data[Object.keys(data)[0]];
    },

    selectBrand(value) {
      if (!isEmpty(this.selectedBrand) && value.id !== this.selectedBrand.id) {
        this.$store.commit('AdvertisementStore/selectModel', undefined);
      }
      this.$store.commit('AdvertisementStore/selectBrand', value);
    },
    selectModel(value) {
      this.$store.commit('AdvertisementStore/selectModel', value);
    },
    selectBody(value) {
      this.$store.commit('AdvertisementStore/selectBody', value);
    },
    selectSeatsCount(value) {
      this.$store.commit('AdvertisementStore/selectSeatsCount', value);
    },
    selectGearbox(value) {
      this.$store.commit('AdvertisementStore/selectGearbox', value);
    },
    selectWheelDrive(value) {
      this.$store.commit('AdvertisementStore/selectWheelDrive', value);
    },
    selectFuel(value) {
      this.$store.commit('AdvertisementStore/selectFuel', value);
    },
    selectDisplacement(value) {
      this.$store.commit('AdvertisementStore/selectDisplacement', value);
    },
    selectEnginePower(value) {
      this.$store.commit('AdvertisementStore/selectEnginePower', value);
    },
    selectCarState(value) {
      this.$store.commit('AdvertisementStore/selectCarState', value);
    },
    selectRidesSinceYear(value) {
      this.$store.commit('AdvertisementStore/selectRidesSinceYear', value);
    },
    selectCountryOrigin(value) {
      this.$store.commit('AdvertisementStore/selectCountryOrigin', value);
    },
    selectFirstOwner(value) {
      this.$store.commit('AdvertisementStore/selectFirstOwner', value);
    },

    async loadParams() {
      const { data } = await ApiService.get(
        `advertisements/${this.advertisementId}`
      );
      return data;
    },

    mapAdvertisementParam(param) {
      return { id: param.id, value: param.name, label: param.name };
    },
  },

  async mounted() {
    const advertisementParams = await this.loadParams();
    this.advertisement = advertisementParams
    await this.$store.commit(
      'AdvertisementStore/modelType',
      advertisementParams.modelType
    );
    this.selectBrand(this.mapAdvertisementParam(advertisementParams.brand));
    this.selectModel(this.mapAdvertisementParam(advertisementParams.model));
    this.selectBody(this.mapAdvertisementParam(advertisementParams.body));
    this.selectGearbox(this.mapAdvertisementParam(advertisementParams.gearbox));
    this.selectWheelDrive(
      this.mapAdvertisementParam(advertisementParams.wheelDrive)
    );
    this.selectFuel(this.mapAdvertisementParam(advertisementParams.fuel));
    this.selectCountryOrigin(
      this.mapAdvertisementParam(advertisementParams.countryOrigin)
    );
    this.selectSeatsCount(
      this.mapAdvertisementParam(advertisementParams.seatsCount)
    );
    this.selectDisplacement({
      value: parseInt(advertisementParams.displacement),
    });
    this.selectEnginePower({ value: parseInt(advertisementParams.power) });
    this.selectCarState({ value: advertisementParams.carState });
    this.selectRidesSinceYear({
      value: parseInt(advertisementParams.ridesSinceYear),
    });
    this.selectFirstOwner({ value: advertisementParams.firstOwner });
    this.$store.commit(
      'AdvertisementStore/mileage',
      parseInt(advertisementParams.mileage)
    );
    this.$store.commit('AdvertisementStore/vin', advertisementParams.vin);
    this.$store.commit(
      'AdvertisementStore/price',
      parseInt(advertisementParams.price)
    );
    this.$store.commit(
      'AdvertisementStore/subtitle',
      advertisementParams.subtitle
    );
    this.$store.commit(
      'AdvertisementStore/advertisementText',
      advertisementParams.advertisementText
    );
    this.$store.commit(
      'AdvertisementStore/vatDeduction',
      advertisementParams.vatDeduction
    );
    this.$store.commit(
      'AdvertisementStore/carEquipments',
      advertisementParams.customEquipments
        .filter((e) => !e.isCustom)
        .map((e) => {
          return { id: e.carEquipmentId, name: e.name, count: 0 };
        })
    );
    this.$store.commit(
      'AdvertisementStore/customEquipments',
      advertisementParams.customEquipments
        .filter((e) => e.isCustom)
        .map((e) => e.name)
    );
    this.$store.commit(
      'AdvertisementStore/customEquipments',
      advertisementParams.customEquipments
        .filter((e) => e.isCustom)
        .map((e) => e.name)
    );
    this.$store.commit('AdvertisementStore/images', advertisementParams.photos);
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Subtle close button styling */
.btn-close {
  padding: 0.25rem !important;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  background: none !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;

  .icon {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    box-shadow: none !important;
    outline: none !important;
  }
}
</style>
